// IndexedDB 스키마 (기획서 7.1) — 5개 스토어
// books, book_files, reading_progress, page_cache, settings

const DB_NAME = 'ebook_reader_db';
const DB_VERSION = 1;

let _dbPromise = null;

export function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = req.result;

      // 7.2 books — 기본키 id, 인덱스 lastReadAt / status
      if (!db.objectStoreNames.contains('books')) {
        const s = db.createObjectStore('books', { keyPath: 'id' });
        s.createIndex('lastReadAt', 'lastReadAt');
        s.createIndex('status', 'status');
      }
      // 7.3 book_files — 기본키 bookId
      if (!db.objectStoreNames.contains('book_files')) {
        db.createObjectStore('book_files', { keyPath: 'bookId' });
      }
      // 7.4 reading_progress — 기본키 bookId
      if (!db.objectStoreNames.contains('reading_progress')) {
        db.createObjectStore('reading_progress', { keyPath: 'bookId' });
      }
      // 7.5 page_cache — 기본키 key, 인덱스 bookId
      if (!db.objectStoreNames.contains('page_cache')) {
        const s = db.createObjectStore('page_cache', { keyPath: 'key' });
        s.createIndex('bookId', 'bookId');
      }
      // 7.6 settings — 기본키 id (항상 "global")
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'id' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB blocked (다른 탭에서 열려 있을 수 있음)'));
  });
  return _dbPromise;
}

// 단일 store 트랜잭션 헬퍼
export async function tx(storeName, mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result;
    try {
      result = fn(store);
    } catch (err) {
      reject(err);
      return;
    }
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'));
  });
}

function reqToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function idbGet(storeName, key) {
  return tx(storeName, 'readonly', (store) => reqToPromise(store.get(key)));
}

export async function idbGetAll(storeName) {
  return tx(storeName, 'readonly', (store) => reqToPromise(store.getAll()));
}

export async function idbPut(storeName, value) {
  return tx(storeName, 'readwrite', (store) => store.put(value));
}

export async function idbDelete(storeName, key) {
  return tx(storeName, 'readwrite', (store) => store.delete(key));
}

// 인덱스로 조회 (page_cache.bookId 일괄 정리용)
export async function idbGetAllByIndex(storeName, indexName, query) {
  return tx(storeName, 'readonly', (store) =>
    reqToPromise(store.index(indexName).getAll(query))
  );
}

// 오래된 page_cache 정리 (기획서 12.3 QuotaExceeded 대응, 7.5 정리 정책)
export async function pruneOldPageCache(perBookLimit = 12) {
  const all = await idbGetAll('page_cache');
  const byBook = new Map();
  for (const rec of all) {
    if (!byBook.has(rec.bookId)) byBook.set(rec.bookId, []);
    byBook.get(rec.bookId).push(rec);
  }
  for (const [, recs] of byBook) {
    if (recs.length <= perBookLimit) continue;
    recs.sort((a, b) => a.generatedAt - b.generatedAt);
    const toDelete = recs.slice(0, recs.length - perBookLimit);
    for (const r of toDelete) await idbDelete('page_cache', r.key);
  }
}

// 쓰기 재시도 — 지수 백오프 250→500→1000ms, 최대 3회 (기획서 12.3)
export async function idbWriteWithRetry(writeFn, { maxRetries = 3 } = {}) {
  let delay = 250;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await writeFn();
    } catch (err) {
      if (err && err.name === 'QuotaExceededError') {
        try {
          await pruneOldPageCache();
        } catch {
          /* 정리 실패는 무시하고 재시도 진행 */
        }
      }
      if (attempt === maxRetries) throw err;
      await new Promise((r) => setTimeout(r, delay));
      delay *= 2;
    }
  }
}
